/**
 * Footer Component for ModelViewer
 *
 * A status bar component that displays navigation instructions, measurement data,
 * depth estimation results, and error messages at the bottom of the 3D model viewer.
 *
 * Features:
 * - Navigation instructions for 3D viewer controls
 * - Real-time measurement display (distance, angle, area, perimeter)
 * - Depth estimation and volume calculation display
 * - Error message display with highlighted styling
 * - Responsive layout with proper spacing
 *
 */

import React from "react";
import { Stack, Typography } from "@mui/material";

/**
 * Props interface for the Footer component
 */
export interface FooterProps {
  /** Whether area measurement tool is currently active */
  showAreaTool: boolean;
  /** Calculated area of the measurement polygon in cm² */
  area: number;
  /** Total perimeter of the measurement polygon in cm */
  perimeter: number;
  /** Distance between first two measurement points in cm */
  distance: number;
  /** Angle between first two measurement points in degrees */
  angle: number;
  /** Whether depth estimation feature is enabled */
  showDepthEstimation?: boolean;
  /** Calculated average depth for volume estimation in cm */
  depthEstimation: number;
  /** Error message to display to user, if any */
  errorMessage: string | null;
}

/**
 * Footer component that displays measurement status and instructions
 * 
 * Positioned as an overlay at the bottom of the ModelViewer component.
 * Shows different content based on the active measurement tool and current state.
 *
 * @param props - Footer component props
 * @returns JSX element containing the status bar
 */
const Footer: React.FC<FooterProps> = ({
  showAreaTool,
  area,
  perimeter,
  distance,
  angle,
  showDepthEstimation = false,
  depthEstimation,
  errorMessage,
}) => {
  /**
   * Generates the appropriate measurement display text based on current tool and values
   * 
   * For area tool: Shows area and perimeter when available, or instruction to click
   * For distance tool: Shows distance and angle when available, or instruction to click
   *
   * @returns Formatted measurement text for display
   */
  const getMeasurementText = (): string => {
    if (showAreaTool) {
      return area <= 0
        ? "Click to measure area"
        : `Area: ${area.toFixed(2)} cm² Perimeter: ${perimeter.toFixed(2)} cm`;
    } else {
      return distance < 0
        ? "Click to measure points"
        : `Distance: ${distance.toFixed(2)} cm, Angle: ${angle.toFixed(2)} degrees`;
    }
  };

  /**
   * Generates depth estimation display text when feature is enabled
   * 
   * Shows calculated depth and estimated volume, or indicates when not calculated
   *
   * @returns Formatted depth estimation text for display
   */
  const getDepthEstimationText = (): string => {
    return depthEstimation > 0
      ? `Average Depth: ${depthEstimation.toFixed(2)} cm Estimated Volume: ${(area * depthEstimation).toFixed(2)} cm³`
      : "Average Depth: Not calculated. Estimated Volume: Not calculated.";
  };

  return (
    <Stack
      direction="row"
      justifyContent="space-evenly"
      alignItems="center"
      spacing={1}
      sx={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        padding: "12px",
        textAlign: "center",
        borderBottomLeftRadius: "0.5rem",
        borderBottomRightRadius: "0.5rem",
        zIndex: 100, // Ensure footer appears above other elements
      }}
    >
      {/* Navigation Instructions Section */}
      <Typography
        variant="body2"
        sx={{
          color: "white",
          marginBottom: "4px",
          flexShrink: 0, // Prevent text from shrinking on small screens
        }}
      >
        Left click and drag to rotate, right click and drag to pan, scroll to zoom
      </Typography>

      {/* Current Measurements Display Section */}
      <Typography
        variant="body2"
        sx={{
          color: "white",
          marginBottom: "4px",
          fontWeight: "medium", // Emphasize measurement data
        }}
      >
        {getMeasurementText()}
      </Typography>

      {/* Depth Estimation Display Section (conditional) */}
      {showDepthEstimation && (
        <Typography
          variant="body2"
          sx={{
            color: "white",
            marginBottom: "4px",
            fontStyle: "italic", // Distinguish depth estimation as advanced feature
          }}
        >
          {getDepthEstimationText()}
        </Typography>
      )}

      {/* Error Message Display Section (conditional) */}
      {errorMessage && (
        <Typography
          variant="body2"
          sx={{
            color: "#f44336", // Material-UI error color (red)
            fontWeight: "bold", // Make errors more prominent
            backgroundColor: "rgba(244, 67, 54, 0.1)", // Subtle error background
            padding: "4px 8px",
            borderRadius: "4px",
          }}
        >
          {errorMessage}
        </Typography>
      )}
    </Stack>
  );
};

export default Footer;
